import { Util } from 'src/shared/util/util';
import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import { Client } from 'src/providers/client/client';
import { Loading } from 'src/shared/components/loading/loading';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { BillingHistoryPage } from 'src/pages/billing-history/billing-history';
import { ReportIssue } from 'src/providers/report-issue/report-issue';
import { DateTime as LuxonDateTime } from 'luxon';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

@Component({
    selector: 'past-jobs',
    templateUrl: 'past-jobs.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['past-jobs.scss'],
    standalone: false
})
export class PastJobsPage implements OnInit {
  cleaningHistory: any;
  form: UntypedFormGroup;
  desktopPropertyForm: UntypedFormGroup;
  shownHistory: any;
  selectedAddress = '';
  addressFilter: any[] = [{ viewValue: 'All Properties', value: 'all' }];
  cantGoBack: boolean;
  addressId: number;
  loaded: boolean;
  totalRecords: number;
  pageSize = 10;
  dateRangeForm: UntypedFormGroup;
  selectOptions: any;
  filters: any = {};
  // Tracks the last applied property selection so we can tell, in the multi-select,
  // whether the user just tapped "All Properties" (which should clear the filter)
  // versus toggling a specific property while "all" still lingers in the selection.
  private lastPropertySelection: any[] = ['all'];
  constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    private route: ActivatedRoute,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public windowService: WindowService,
    private reportIssue: ReportIssue,
    private util: Util,
    public onboardingProvider: OnboardingProvider
  ) {
  }

  @Loading('', true)
  async ngOnInit(): Promise<any> {
    try {
      this.onboardingProvider.showOnboarding = false;
      this.form = this.fb.group({
        // `tidy-select` is used in multi-select mode on mobile; it must always be an array.
        property: [['all']],
      });
      // Desktop `tidy-header-select` expects scalar values, not arrays.
      this.desktopPropertyForm = this.fb.group({
        property: ['all'],
      });
      this.dateRangeForm = this.fb.group({
        start: '',
        end: '',
        customStringSelect: ''
      });
      this.selectOptions = [
        { viewValue: 'Custom', value: 'custom' },
        { viewValue: 'Today', value: 'today' },
        { viewValue: 'Last Week', value: 'last_week' },
        { viewValue: 'Last Month', value: 'last_month' },
        { viewValue: 'All Time', value: 'all_time' }
      ];
      this.dateRangeForm.patchValue({ customStringSelect: 'all_time' });
      this.addressId = Number(this.navCtrl.getParam('addressId')) || 0;
      if (this.addressId && this.addressId !== 0) {
        this.filters['address_ids'] = [this.addressId];
        this.form.patchValue({ property: [this.addressId] });
        this.desktopPropertyForm.patchValue({ property: this.addressId });
        this.lastPropertySelection = [this.addressId];
      }
      await this.selectStringDateRange('all_time');
      this.cantGoBack = this.route.snapshot.data.cantGoBack;
      await this.populateAddressFilter();
      this.loaded = true;
    } catch (err) {
      console.error(err)
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async loadCleaningsHistory(page = 1, perPage = 10, filters = {}) {
    const loading = await this.util.showLoading();
    try {
      const cleaningHistory =
        await this.client.getCleaningsHistoryWithTotalRecords(page, perPage, filters);
      this.totalRecords = cleaningHistory.totalRecords;
      this.cleaningHistory = cleaningHistory.body.flatMap((cleaning) => {
        cleaning.homekeeper_jobs = cleaning?.homekeeper_jobs || [
          {
            homekeeper: {
              name: cleaning?.homekeeper,
            },
          },
        ];
        const status = this.reportIssue.getJobStatusData(
          cleaning?.homekeeper_jobs,
          cleaning?.job_progress,
          cleaning
        );
        cleaning.status = status;
        if (cleaning.cleaning_id !== null) {
          return cleaning;
        } else {
          return [];
        }
      });
      if (this.addressId !== 0) {
        this.shownHistory = this.cleaningHistory.filter(
          (item) => item.address_id === this.addressId
        );
      } else {
        this.shownHistory = this.cleaningHistory;
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    } finally {
      loading.dismiss();
      window.scrollTo(0, 0);
    }
  }

  selectPage(pagination: {
    pageIndex: number;
    pageSize: number;
    previousPageIndex: number;
    length: number;
  }) {
    this.loadCleaningsHistory(pagination.pageIndex + 1, pagination.pageSize, this.filters);
  }

  selectCustomDateRange(selection) {
    const start = new Date(selection.start);
    const end = new Date(selection.end);
    const startDate = LuxonDateTime.fromJSDate(start).toISODate();
    const endDate = LuxonDateTime.fromJSDate(end).toISODate();
    this.filters['from_date'] = startDate;
    this.filters['until_date'] = endDate;
    this.loadCleaningsHistory(1, this.pageSize, this.filters);
  }

  async selectStringDateRange(selection) {
    const today = LuxonDateTime.local();
    let startDate = today.toISODate();
    let endDate = today.toISODate();
    if (selection === 'last_week') {
      startDate = today.minus({ days: 7 }).toISODate();
    }
    if (selection === 'last_month') {
      startDate = today.minus({ months: 1 }).toISODate();
    }
    if (selection === 'all_time') {
      startDate = null;
      endDate = null;
    }
    this.filters['from_date'] = startDate;
    this.filters['until_date'] = endDate;
    await this.loadCleaningsHistory(1, this.pageSize, this.filters);
  }

  parseStatus(cleaning: any): string {
    if (!cleaning?.job_progress) {
      return cleaning?.status || '';
    }
    const parsedStatus = cleaning.job_progress
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return parsedStatus;
  }

  async goToBillingHistory() {
    const hasProperties = await this.onboardingProvider.checkIfHasProperties();
    if (!hasProperties) {
      return this.util.showError('Please add a property before viewing your billing history.');
    }
    this.goToPage('billing-history', {}, BillingHistoryPage);
  }

  async populateAddressFilter() {
    this.addressFilter = this.navCtrl.getParam('addressFilter');
    if (this.addressFilter === null) {
      const addresses = await this.client.getAddresses();
      this.addressFilter = await this.client.parseAddressList(addresses, true);
    }
    this.addressFilter.pop();
    this.addressFilter.unshift({ viewValue: 'All Properties', value: 'all' });
  }

  goToCleaningDetail(cleaning) {
    localStorage.setItem('pastJobBackPage', 'past-jobs');
    if (cleaning.is_private) {
      this.navCtrl.navigateForward([
        `past-private-job/${cleaning.cleaning_id}`,
      ]);
    } else {
      this.navCtrl.navigateForward([`past-job/${cleaning.cleaning_id}`]);
    }
  }

  changeAddress(addressIds) {
    // `tidy-select` emits either a scalar (desktop) or an array (mobile multi-select).
    // We include a sentinel "all" option in the list which should *remove* the filter.
    const selection = Array.isArray(addressIds) ? addressIds : [addressIds];
    const cleaned = selection.filter((v) => v !== null && v !== undefined && v !== '');
    const specifics = cleaned.filter((v) => v !== 'all');

    // In the multi-select, tapping "All Properties" adds "all" alongside the
    // already-selected property (e.g. [123, 'all']). Detect that the user just
    // picked "all" (it wasn't selected before) and reset to All Properties.
    const allWasSelected = this.lastPropertySelection.includes('all');
    const allJustSelected = cleaned.includes('all') && !allWasSelected;

    // Once the user manually changes the property filter, the deep-link
    // `addressId` constraint no longer applies (see loadCleaningsHistory).
    this.addressId = 0;

    if (allJustSelected || specifics.length === 0) {
      delete this.filters['address_ids'];
      this.lastPropertySelection = ['all'];
      // Keep the UI consistent: multi-select should show "All Properties" as the only selection.
      this.form.get('property')?.setValue(['all'], { emitEvent: false });
      this.desktopPropertyForm.get('property')?.setValue('all', { emitEvent: false });
    } else {
      // Ensure we never send "all" to the API.
      this.filters['address_ids'] = specifics;
      this.lastPropertySelection = specifics;
      // Keep the UI consistent: drop "all" if a specific property is selected.
      this.form.get('property')?.setValue(specifics, { emitEvent: false });
      // Desktop only supports single selection, use the first selected value.
      this.desktopPropertyForm.get('property')?.setValue(specifics[0], { emitEvent: false });
    }
    this.loadCleaningsHistory(1, this.pageSize, this.filters);
  }

  goBack() {
    this.navCtrl.navigateBack('more');
  }

  async goToPage(url, params, component = null) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve(
        'dialog-right-side-open'
      );
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
